import type { BillingCandidateEntry, BillingDraftDto } from '@fos/shared/contracts/billing';
import { reportPeriodRange } from '@fos/shared/contracts/time';
import { billingPeriodRangeText } from '@fos/shared/labels';
import { amountByRate } from '@fos/shared/money';
import { todayLocal } from '@fos/shared/time';
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Mono } from '../../../ui/Mono';
import { Segmented } from '../../../ui/Segmented';
import { ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { ClientPicker } from '../../clients';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { clockText, dayMonthText, hoursMinutesText, hoursRangeText, moneyAuto, moneyShort } from '../billing-format';

type PeriodMode = 'last_month' | 'this_month' | 'custom';

const PERIOD_OPTIONS: Array<{ value: PeriodMode; label: string }> = [
  { value: 'last_month', label: 'Poprzedni miesiąc' },
  { value: 'this_month', label: 'Ten miesiąc' },
  { value: 'custom', label: 'Zakres' },
];

export interface NewDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** klient z góry (np. „Przygotuj zestawienie” w sekcji „Do rozliczenia”) */
  clientId?: string | null;
  /** okres z góry (daty lokalne); bez niego — poprzedni miesiąc */
  periodFrom?: string;
  periodTo?: string;
  onCreated?: (draft: BillingDraftDto) => void;
}

/**
 * Nowe zestawienie (Figma 09 „Nowe zestawienie”, „Przygotuj zestawienie”): klient, okres (poprzedni miesiąc, ten
 * miesiąc, zakres) i niezafakturowane wpisy klienta z okresu — domyślnie wszystkie zaznaczone. Kwoty tylko
 * z „Widzi stawki i kwoty”. Utworzenie = zestawienie robocze; faktura dopiero po akceptacji.
 */
export function NewDraftDialog({ open, onOpenChange, clientId, periodFrom, periodTo, onCreated }: NewDraftDialogProps) {
  const access = useAccess();
  const seesRates = access.flag('seesRates');
  const utils = trpc.useUtils();
  const create = trpc.billing.createDraft.useMutation();
  const today = todayLocal();
  const [client, setClient] = useState<string | null>(clientId ?? null);
  const [mode, setMode] = useState<PeriodMode>(periodFrom && periodTo ? 'custom' : 'last_month');
  const [from, setFrom] = useState(periodFrom ?? '');
  const [to, setTo] = useState(periodTo ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setClient(clientId ?? null);
    setMode(periodFrom && periodTo ? 'custom' : 'last_month');
    setFrom(periodFrom ?? '');
    setTo(periodTo ?? '');
    setError(null);
    setFieldErrors({});
  }, [open, clientId, periodFrom, periodTo]);

  const period = useMemo(() => {
    if (mode === 'custom') return from && to && from <= to ? { from, to } : null;
    return reportPeriodRange(mode, today);
  }, [mode, from, to, today]);

  const candidates = trpc.billing.candidates.useQuery(
    { clientId: client ?? '', periodFrom: period?.from, periodTo: period?.to },
    { enabled: open && Boolean(client) && Boolean(period) },
  );
  // od najnowszego, niezależnie od grupy (projekt / bez projektu) — jak tabela zestawienia
  const entries = useMemo<BillingCandidateEntry[]>(
    () =>
      (candidates.data?.groups ?? [])
        .flatMap((g) => g.entries)
        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()),
    [candidates.data],
  );
  // po wczytaniu kandydatów: wszystkie zaznaczone
  useEffect(() => {
    setSelected(new Set(entries.map((e) => e.id)));
  }, [entries]);

  const chosen = entries.filter((e) => selected.has(e.id));
  const chosenSeconds = chosen.reduce((s, e) => s + e.durationSeconds, 0);
  // kwota zestawienia wg reguły pozycji: stawka × łączny czas wpisów o tej stawce (jak suma na serwerze)
  const currency = candidates.data?.currency ?? 'PLN';
  const chosenAmount = seesRates
    ? amountByRate(
        chosen.map((e) => ({ rate: e.rate ?? null, seconds: e.durationSeconds, currency })),
        currency,
      )
    : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!client) {
      setFieldErrors({ clientId: 'Wybierz klienta' });
      return;
    }
    if (!period) {
      setFieldErrors({ periodFrom: 'Podaj początek i koniec okresu' });
      return;
    }
    try {
      const draft = await create.mutateAsync({
        clientId: client,
        periodFrom: period.from,
        periodTo: period.to,
        entryIds: chosen.map((e) => e.id),
      });
      await utils.billing.invalidate();
      toast.success(`Utworzono zestawienie ${draft.label}`);
      onOpenChange(false);
      onCreated?.(draft);
    } catch (err) {
      if (appErrorCode(err) === 'VALIDATION_ERROR') {
        const issues = validationIssues(err);
        if (issues.length > 0) {
          setFieldErrors(Object.fromEntries(issues.map((i) => [i.path.split('.')[0] ?? '', i.message])));
        }
      }
      setError(errorMessage(err));
    }
  };

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  let list: ReactNode = null;
  if (client && period) {
    if (candidates.isPending) {
      list = (
        <div role="status" aria-label="Wczytywanie wpisów" className="flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      );
    } else if (candidates.isError) {
      list = (
        <ErrorState
          compact
          title="Nie udało się wczytać wpisów"
          onRetry={() => void candidates.refetch()}
          retrying={candidates.isFetching}
        />
      );
    } else if (entries.length === 0) {
      const outside = candidates.data?.outsidePeriod ?? 0;
      list = (
        <p className="rounded-lg bg-surface px-4 py-3 text-body-s text-fg-secondary">
          Brak niezafakturowanych wpisów w tym okresie.
          {outside > 0 &&
            ` Poza okresem: ${outside} ${plural(outside, ['wpis', 'wpisy', 'wpisów'])} — zmień okres albo utwórz puste zestawienie i dodaj je później.`}
        </p>
      );
    } else {
      const all = chosen.length === entries.length;
      list = (
        <div className="flex flex-col">
          <div className="flex items-center gap-3 border-b border-line pb-2">
            <Checkbox
              checked={all}
              onCheckedChange={(on) => setSelected(on ? new Set(entries.map((e) => e.id)) : new Set())}
              label={`Wpisy · ${chosen.length} z ${entries.length}`}
            />
            <span className="flex-1" />
            <Mono className="text-caption text-fg-secondary">
              {hoursMinutesText(chosenSeconds)}
              {chosenAmount !== null && ` · ${moneyShort(chosenAmount, candidates.data?.currency)}`}
            </Mono>
          </div>
          <ul className="max-h-[300px] overflow-y-auto" aria-label="Wpisy do zestawienia">
            {entries.map((e) => (
              <li key={e.id} className="flex min-h-10 items-center gap-3 border-b border-line py-1.5">
                <Checkbox
                  checked={selected.has(e.id)}
                  onCheckedChange={(on) => toggle(e.id, on)}
                  label={
                    <span className="flex min-w-0 items-baseline gap-2">
                      <Mono className="text-caption text-fg-muted">{dayMonthText(e.date)}</Mono>
                      <span className="min-w-0 truncate text-fg">{e.description || 'Praca'}</span>
                    </span>
                  }
                  className="min-w-0 flex-1"
                />
                <Mono className="hidden text-caption text-fg-muted sm:inline">
                  {hoursRangeText(e.startedAt, e.endedAt)}
                </Mono>
                <Mono className="w-12 text-right text-caption text-fg">{clockText(e.durationSeconds)}</Mono>
                {seesRates && e.amount !== undefined && (
                  <Mono className="hidden w-20 text-right text-caption text-fg sm:inline">
                    {moneyAuto(e.amount, candidates.data?.currency)}
                  </Mono>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Nowe zestawienie"
      className={FULLSCREEN_ON_PHONE}
      description="Zestawienie zbiera niezafakturowany czas klienta. Fakturę wystawimy dopiero po Twojej akceptacji."
      width="l"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="new-billing-draft" loading={create.isPending}>
            Utwórz zestawienie
          </Button>
        </>
      }
    >
      <form id="new-billing-draft" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <Field label="Klient" error={fieldErrors.clientId}>
          <ClientPicker value={client} onChange={setClient} excludePartial />
        </Field>
        <Field label="Okres" error={fieldErrors.periodFrom ?? fieldErrors.periodTo}>
          <div className="flex flex-col gap-2.5">
            <Segmented aria-label="Okres" value={mode} onValueChange={setMode} options={PERIOD_OPTIONS} fullWidth />
            {mode === 'custom' ? (
              <div className="grid grid-cols-2 gap-2.5">
                <DateInput aria-label="Od" value={from} onValueChange={setFrom} />
                <DateInput aria-label="Do" value={to} onValueChange={setTo} />
              </div>
            ) : (
              period && (
                <span className="text-caption text-fg-muted">{billingPeriodRangeText(period.from, period.to)}</span>
              )
            )}
          </div>
        </Field>
        {list}
      </form>
    </Dialog>
  );
}
